// Core business logic services for FinList (no UI).
// Uses the data models defined in financeModels.js and aligns with guardrails.
// All operations are scoped to users/{uid}/... and never touch external/bank systems.

import {
  collection,
  doc,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

import {
  createIncome,
  createExpense,
  createExpenseFromShoppingItem,
  createSavingsTransaction,
  createTodo,
  createBudgetPeriod,
} from '@/models/financeModels';

import {
  incomePath,
  expensesPath,
  shoppingListItemsPath,
  budgetsPath,
  savingsGoalsPath,
  savingsTransactionsPath,
  todosPath,
} from '@/config/firestoreCollections';

const pad2 = (num) => String(num).padStart(2, '0');

// Normalize amounts coming from user input (numbers or loosely formatted strings).
const toNonNegativeAmount = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const sanitized = typeof value === 'string'
    ? value.replace(/[^0-9,.-]/g, '').replace(',', '.')
    : value;
  const normalized = Number(sanitized);
  if (Number.isNaN(normalized) || normalized < 0) {
    throw new Error('amount must be a non-negative number');
  }
  return normalized;
};

// Derive a monthly period id like "2025-11" from a Date.
const deriveMonthlyPeriodId = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

// Ensure a budget doc exists so increments don't fail. Creates with merge:true.
const ensureBudgetDoc = async (userId, periodId, type = 'monthly', startDate = serverTimestamp(), endDate = serverTimestamp()) => {
  const budgetRef = doc(db, budgetsPath(userId), periodId);
  await setDoc(
    budgetRef,
    {
      ...createBudgetPeriod(userId, {
        id: periodId,
        type,
        startDate,
        endDate,
      }),
      id: periodId, // preserve id when merging
    },
    { merge: true }
  );
  return budgetRef;
};

/**
 * addIncome - creates an income doc and bumps the relevant budget actualIncome.
 * @param {Object} payload { userId, amount, source, currency, date, periodId?, periodType? }
 * Expected: user-scoped write, no external integrations; budget actualIncome increments by amount.
 */
export const addIncome = async (payload) => {
  if (!payload?.userId) throw new Error('userId is required');
  const amount = toNonNegativeAmount(payload.amount);

  const userId = payload.userId;
  const date = payload.date || new Date();
  const periodId = payload.periodId || deriveMonthlyPeriodId(date);
  const periodType = payload.periodType || 'monthly';

  const income = createIncome(userId, { ...payload, amount, date });
  const incomeRef = await addDoc(collection(db, incomePath(userId)), income);

  const budgetRef = await ensureBudgetDoc(userId, periodId, periodType, date, date);
  await updateDoc(budgetRef, {
    actualIncome: increment(amount),
    updatedAt: serverTimestamp(),
  });

  return { incomeId: incomeRef.id, periodId };
};

/**
 * addExpense - creates an expense and updates budget totals/categoryActual.
 * @param {Object} payload { userId, amount, description, category, currency, date, periodId?, periodType? }
 * Expected: user-scoped write; budget actualExpenses increments and categoryActual[category] increments by amount.
 */
export const addExpense = async (payload) => {
  if (!payload?.userId) throw new Error('userId is required');
  const amount = toNonNegativeAmount(payload.amount);

  const userId = payload.userId;
  const date = payload.date || new Date();
  const periodId = payload.periodId || deriveMonthlyPeriodId(date);
  const periodType = payload.periodType || 'monthly';
  const category = payload.category || 'other';

  const expense = createExpense(userId, { ...payload, amount, date, category });
  const expenseRef = await addDoc(collection(db, expensesPath(userId)), expense);

  const budgetRef = await ensureBudgetDoc(userId, periodId, periodType, date, date);
  await updateDoc(budgetRef, {
    actualExpenses: increment(amount),
    [`categoryActual.${category}`]: increment(amount),
    updatedAt: serverTimestamp(),
  });

  return { expenseId: expenseRef.id, periodId };
};

/**
 * markShoppingItemAsBought - marks item bought, creates linked expense, updates budget.
 * @param {Object} params { userId, listId, itemId, pricePerUnit, quantity }
 * Expected: status forced to 'bought'; creates an expense with source 'shopping_list'; budget actualExpenses + categoryActual updated by pricePerUnit*quantity.
 */
export const markShoppingItemAsBought = async ({ userId, listId, itemId, pricePerUnit, quantity }) => {
  if (!userId || !listId || !itemId) throw new Error('userId, listId, and itemId are required');
  const unitPrice = toNonNegativeAmount(pricePerUnit);
  const qty = Number(quantity) || 1;
  if (Number.isNaN(qty) || qty < 0) throw new Error('quantity must be >= 0');

  // Fetch the shopping item to reuse its metadata (category, name, etc.)
  const itemRef = doc(db, shoppingListItemsPath(userId, listId), itemId);
  const itemSnap = await getDoc(itemRef);
  if (!itemSnap.exists()) throw new Error('Shopping item not found');
  const item = { id: itemId, ...itemSnap.data() };

  // Update item status and purchase info (non-destructive to other records).
  const subtotal = Number((unitPrice * qty).toFixed(2));
  await updateDoc(itemRef, {
    // Align with shopping list status enum used across the app.
    status: 'purchased',
    boughtUnitPrice: unitPrice,
    quantity: qty,
    subtotal,
    boughtAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Create linked expense from this shopping item.
  const expense = createExpenseFromShoppingItem(userId, item, {
    amount: unitPrice * qty,
    shoppingItemId: itemId,
    category: item.category || 'other',
    date: serverTimestamp(),
  });
  // Guard against Firestore rejecting undefined fields.
  Object.keys(expense).forEach((key) => {
    if (expense[key] === undefined) {
      delete expense[key];
    }
  });
  const expenseRef = await addDoc(collection(db, expensesPath(userId)), expense);
  await updateDoc(itemRef, {
    linkedExpenseId: expenseRef.id,
  });

  // Update budget with the expense.
  const periodId = deriveMonthlyPeriodId(new Date());
  const budgetRef = await ensureBudgetDoc(userId, periodId, 'monthly', new Date(), new Date());
  await updateDoc(budgetRef, {
    actualExpenses: increment(expense.amount),
    [`categoryActual.${expense.category}`]: increment(expense.amount),
    updatedAt: serverTimestamp(),
  });

  return { expenseId: expenseRef.id, periodId };
};

/**
 * addSavingsTransaction - records a deposit/withdrawal and updates goal + budget actualSavings.
 * @param {Object} payload { userId, goalId, amount, type ('deposit'|'withdrawal'), date, periodId?, periodType? }
 * Expected: goal.currentSaved increments by signed amount; budget.actualSavings increments by same signed amount; no silent changes elsewhere.
 */
export const addSavingsTransaction = async (payload) => {
  if (!payload?.userId) throw new Error('userId is required');
  if (!payload?.goalId) throw new Error('goalId is required');
  const amount = Number(payload.amount);
  if (isNaN(amount) || amount < 0) throw new Error('amount must be >= 0');

  const userId = payload.userId;
  const goalId = payload.goalId;
  const date = payload.date || new Date();
  const periodId = payload.periodId || deriveMonthlyPeriodId(date);
  const periodType = payload.periodType || 'monthly';
  const type = payload.type === 'withdrawal' ? 'withdrawal' : 'deposit';
  const signedAmount = type === 'withdrawal' ? -amount : amount;

  const tx = createSavingsTransaction(userId, goalId, { ...payload, amount, type, date });
  const txRef = await addDoc(collection(db, savingsTransactionsPath(userId)), tx);

  // Update goal balance
  const goalRef = doc(db, savingsGoalsPath(userId), goalId);
  await updateDoc(goalRef, {
    currentSaved: increment(signedAmount),
    updatedAt: serverTimestamp(),
  });

  // Update budget actualSavings
  const budgetRef = await ensureBudgetDoc(userId, periodId, periodType, date, date);
  await updateDoc(budgetRef, {
    actualSavings: increment(signedAmount),
    updatedAt: serverTimestamp(),
  });

  return { transactionId: txRef.id, periodId };
};

/**
 * addTodo - creates a financial To-Do (non-destructive, read-only links to other docs).
 * @param {Object} payload { userId, title, description?, status?, dueDate?, priority?, isFinancialTask?, estimatedAmount?, relatedType?, relatedRef? }
 */
export const addTodo = async (payload) => {
  if (!payload?.userId) throw new Error('userId is required');
  const userId = payload.userId;
  const todo = createTodo(userId, payload);
  const todoRef = await addDoc(collection(db, todosPath(userId)), todo);
  return { todoId: todoRef.id };
};

// Summary of module connections (human-friendly notes):
// - addIncome: income -> budget.actualIncome
// - addExpense: expense -> budget.actualExpenses + categoryActual
// - markShoppingItemAsBought: shopping item -> expense (source shopping_list) -> budget updates; item status locked to allowed values
// - addSavingsTransaction: savings transaction -> goal.currentSaved -> budget.actualSavings
// - addTodo: creates actionable reminders with read-only links to other records
